import {
  Accordion,
  Box,
  Grid,
  GridItem,
  Text,
  useBreakpointValue,
  useToast
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { dimensionsKeys, showErrorToast } from "../../../../../../utils";
import { useState } from "react";
import * as React from "react";
import {
  DimensionFilterAccordionItem,
  DimensionScopeSection,
  DimensionScopeThreeSection
} from "./index";
import { useGetDimensionsReport } from "../../../../../../hooks/query/reports";
import { getFilledCategoriesArray } from "./DimensionScopeSection";
import {
  getLowestFilteredDimensionKey,
  getNextLowestFilteredDimensionKey
} from "./helpers";

function DimensionReport({
                           dimensionsData,
                           locationElectricityItems,
                           organizationStructure,
                           updateFilter,
                           filtersData
                         }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "reportsPage"
  });
  const filterColumnSpan = useBreakpointValue({
    xl: 3,
    base: 11
  });
  const reportColumnSpan = useBreakpointValue({
    xl: 8,
    base: 11
  });
  const toast = useToast();
  const { reportId } = dimensionsData;
  const [reportData, setReportData] = useState(null);

  const getReportParams = () => {
    const reportParams = {
      requestParams: {},
      currentDimension: null,
      queryParams: ""
    };

    let queryParams = "";
    let highQueryParams = "";
    if (filtersData && Object.keys(filtersData).length > 0) {
      const lowestFilteredDimensionKey = getLowestFilteredDimensionKey(filtersData);
      filtersData[lowestFilteredDimensionKey].items.forEach((item, itemIndex) => {
        queryParams += `${itemIndex === 0 ? "" : "&"}${lowestFilteredDimensionKey}=${item.id}`;
      });
      reportParams.requestParams[lowestFilteredDimensionKey] = queryParams;
      const higherLevelFilteredDimensionKey = getNextLowestFilteredDimensionKey(filtersData);
      if (higherLevelFilteredDimensionKey) {
        filtersData[higherLevelFilteredDimensionKey].items.forEach((item, itemIndex) => {
          highQueryParams += `${itemIndex === 0 ? "" : "&"}${higherLevelFilteredDimensionKey}=${item.id}`;
        });
        reportParams.requestParams[higherLevelFilteredDimensionKey] = highQueryParams;
      }
      reportParams["currentDimension"] = lowestFilteredDimensionKey;
      reportParams["queryParams"] = queryParams;
    }

    return reportParams;
  };

  useGetDimensionsReport(reportId, Object.values(getReportParams().requestParams).join("&"),
    {
      onSuccess: (data) => {
        const foundDimensionObj = dimensionsData[getReportParams().currentDimension];
        const isCountry = foundDimensionObj ? foundDimensionObj.country : false;

        setReportData({
          ...data,
          isCountry: isCountry
        });
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0,
      enabled: filtersData && Object.keys(filtersData).length > 0 && getReportParams().queryParams !== ""
    }
  );

  const hasNoData = () => {
    // If any filters are selected by the user, but there is no direct/indirect emissions data nor country related data available
    // then we show message for missing data.
    return filtersData && Object.keys(filtersData).length !== 0 &&
      (!reportData || (
        getFilledCategoriesArray(reportData.directEmissions).length === 0 &&
        getFilledCategoriesArray(reportData.indirectEmissions).length === 0 &&
        reportData.scopeThreeEmissions?.scopeThreeTotalCo2Emissions === 0 &&
        (reportData.isCountry !== true ||
          (reportData.isCountry === true && !locationElectricityItems) ||
          (reportData.isCountry === true && locationElectricityItems && locationElectricityItems.length === 0)
        )
      ));
  };
  

  return (
    <Grid templateColumns="repeat(11, 1fr)" gap="5">
      <GridItem maxHeight={500} overflow="auto" colSpan={filterColumnSpan}>
        <Box borderRight="1px solid" borderLeft="1px solid" borderColor="gray.200" borderRadius="md">
          <Accordion defaultIndex={[0]} allowToggle={true}>
            {dimensionsKeys.filter(dimKey => organizationStructure.dimensions[dimKey])
              .map(dimKey => {
                return <DimensionFilterAccordionItem
                  key={`${dimKey}-filter-accordion-item`}
                  dimensionKey={dimKey}
                  data={dimensionsData[dimKey]}
                  setDimensionsItems={(itemsIds) => updateFilter(dimKey, dimensionsData[dimKey], itemsIds)}
                  filtersData={filtersData}
                />;
              })}
          </Accordion>
        </Box>
      </GridItem>

      <GridItem maxHeight={500} overflow="auto" colSpan={reportColumnSpan}>
        {hasNoData() && <Text fontSize="lg" fontWeight="bold">
          {t("scope1&2.dimension.noFilteredData")}
        </Text>}

        {reportData && filtersData && Object.keys(filtersData).length > 0 &&
          <>
            <DimensionScopeSection
              title={t("scope1Title")}
              data={reportData.directEmissions}
              manualEmissions={(reportData.manualEmissions ?? []).filter(m => m.scopeCategoryKey === "SCOPE_1")}
              hasOtherData={!hasNoData()}
            />
            <DimensionScopeSection
              title={t("scope2Title")}
              data={reportData.indirectEmissions}
              manualEmissions={(reportData.manualEmissions ?? []).filter(m => m.scopeCategoryKey === "SCOPE_2")}
              hasOtherData={!hasNoData()}
            />
            <DimensionScopeThreeSection
              data={reportData.scopeThreeEmissions}
              manualEmissions={(reportData.manualEmissions ?? []).filter(m => m.scopeCategoryKey !== "SCOPE_1" && m.scopeCategoryKey !== "SCOPE_2")}
              hasOtherData={!hasNoData()}
            />
          </>
        }
      </GridItem>
    </Grid>

  );
}

export default DimensionReport;